import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';
import { defineListQuery } from './list-query.schemas';
import { loginCodeChannelSchema } from './login-code.schemas';

/**
 * `POST /auth/driver/send-code` — answers 204 whether or not the number belongs to a driver. An app from before the
 * channel choice sends none and says it texted, so a missing channel is SMS.
 */
export const sendDriverCodeSchema = z.object({ phone: kePhoneSchema, channel: loginCodeChannelSchema.default('sms') }).strict();
export type SendDriverCodeInput = z.infer<typeof sendDriverCodeSchema>;

/** `POST /auth/driver/verify-code`. `deviceId` is generated by the app once per install and stays the same across refreshes. */
export const verifyDriverCodeSchema = z
  .object({
    phone: kePhoneSchema,
    code: z.string().regex(/^\d{6}$/, 'Expected the 6-digit code'),
    deviceId: z.string().trim().min(1).max(128),
    /** What the office sees in the devices panel; an older app sends none. */
    deviceModel: z.string().trim().min(1).max(128).optional(),
  })
  .strict();
export type VerifyDriverCodeInput = z.infer<typeof verifyDriverCodeSchema>;

/** `POST /auth/driver/refresh` — the refresh token is in the body, not a bearer header, since it must work once the device token has expired. */
export const refreshDriverTokenSchema = z.object({ refreshToken: z.string().min(1).max(256) }).strict();
export type RefreshDriverTokenInput = z.infer<typeof refreshDriverTokenSchema>;

/** Both are opaque; expiry is an ISO instant. The refresh token is replaced on every refresh, so the app stores the newest pair. */
export interface DriverAuthTokens {
  deviceToken: string;
  deviceTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

/** The admin behind a session that acts as a driver (staging only); null on a driver's own session. */
export interface ActingUser {
  id: string;
  name: string;
}

/** `GET /auth/driver/me`. */
export interface DriverSelf {
  id: string;
  name: string;
  phone: string;
  actingUser: ActingUser | null;
}

/** `GET /auth/driver/impersonation`: whether an admin may act as a driver here. Always false in production. */
export interface DriverImpersonationStatus {
  enabled: boolean;
}

/**
 * `POST /auth/driver/impersonation/verify` (the office sign-in code, `verifySignInCodeSchema`) answers with a short-lived
 * pass. It is a bearer token for picking a driver, and nothing else: it opens no office session.
 */
export interface DriverImpersonationPass {
  passToken: string;
  expiresAt: string;
  actingUser: ActingUser;
}

/** `GET /auth/driver/impersonation/drivers` (bearer: the pass). Active drivers only, so there is no status filter. */
export const actableDriverListQuerySchema = defineListQuery({ sortFields: ['name'], defaultSort: { sort: 'name', order: 'asc' } });
export type ActableDriverListQuery = z.infer<typeof actableDriverListQuerySchema>;

export interface ActableDriver {
  id: string;
  name: string;
  phone: string;
  /** The driver's availability (`on_trip`, `resting`, …), so the picker can say who is mid-trip. */
  availability: string;
}

/** `POST /auth/driver/impersonation` (bearer: the pass, which it uses up). */
export const startDriverImpersonationSchema = z.object({ driverId: z.string().uuid(), deviceId: z.string().trim().min(1).max(128) }).strict();
export type StartDriverImpersonationInput = z.infer<typeof startDriverImpersonationSchema>;

export interface DriverImpersonationSession extends DriverAuthTokens {
  driver: { id: string; name: string; phone: string };
  actingUser: ActingUser;
}
